import type { GameNetworkClient } from "../network/GameNetworkClient";

export interface AccountProfile {
  id: string;
  username: string;
  createdAt: number;
}

interface SessionResponse {
  ok: true;
  authRequired: boolean;
  authenticated: boolean;
  registrationEnabled: boolean;
  account?: AccountProfile;
}

interface AccountResponse {
  ok: true;
  account: AccountProfile;
}

interface ErrorResponse {
  ok: false;
  message?: string;
}

export async function waitForAccount(
  network: GameNetworkClient,
): Promise<AccountProfile | undefined> {
  const authPanel = element<HTMLElement>("auth-panel");
  const connectPanel = element<HTMLElement>("connect-panel");
  const connectionPill = element<HTMLElement>("connection-pill");
  const authError = element<HTMLElement>("auth-error");
  const registerSection = element<HTMLElement>("register-section");
  const loginForm = element<HTMLFormElement>("login-form");
  const registerForm = element<HTMLFormElement>("register-form");

  const session = await request<SessionResponse>(
    network.getApiUrl("/auth/session"),
  ).catch((error) => {
    authPanel.classList.remove("hidden");
    authError.textContent = messageFor(error);
    return undefined;
  });

  if (session && (!session.authRequired || session.authenticated)) {
    activateGameShell(session.account, network);
    return session.account;
  }

  authPanel.classList.remove("hidden");
  connectPanel.classList.add("hidden");
  connectionPill.classList.add("hidden");
  registerSection.classList.toggle(
    "hidden",
    !session?.registrationEnabled,
  );

  return new Promise<AccountProfile>((resolve) => {
    let pending = false;
    const finish = (account: AccountProfile): void => {
      activateGameShell(account, network);
      resolve(account);
    };

    loginForm.addEventListener("submit", (event) => {
      event.preventDefault();
      if (pending) {
        return;
      }
      pending = true;
      setFormsDisabled(true);
      authError.textContent = "";
      void request<AccountResponse>(network.getApiUrl("/auth/login"), {
        method: "POST",
        body: JSON.stringify({
          username: element<HTMLInputElement>("login-username").value,
          password: element<HTMLInputElement>("login-password").value,
        }),
      })
        .then((response) => finish(response.account))
        .catch((error) => {
          authError.textContent = messageFor(error);
          pending = false;
          setFormsDisabled(false);
        });
    });

    registerForm.addEventListener("submit", (event) => {
      event.preventDefault();
      if (pending) {
        return;
      }
      pending = true;
      setFormsDisabled(true);
      authError.textContent = "";
      void request<AccountResponse>(
        network.getApiUrl("/auth/register"),
        {
          method: "POST",
          body: JSON.stringify({
            username:
              element<HTMLInputElement>("register-username").value,
            password:
              element<HTMLInputElement>("register-password").value,
            registrationCode:
              element<HTMLInputElement>("registration-code").value,
          }),
        },
      )
        .then((response) => finish(response.account))
        .catch((error) => {
          authError.textContent = messageFor(error);
          pending = false;
          setFormsDisabled(false);
        });
    });
    authPanel.dataset.ready = "true";
  });
}

function activateGameShell(
  account: AccountProfile | undefined,
  network: GameNetworkClient,
): void {
  element<HTMLElement>("auth-panel").classList.add("hidden");
  element<HTMLElement>("connect-panel").classList.remove("hidden");
  element<HTMLElement>("connection-pill").classList.remove("hidden");

  const accountBar = element<HTMLElement>("account-bar");
  const playerName = element<HTMLInputElement>("player-name");
  if (!account) {
    accountBar.classList.add("hidden");
    playerName.readOnly = false;
    return;
  }

  element<HTMLElement>("account-name").textContent = account.username;
  accountBar.classList.remove("hidden");
  playerName.value = account.username;
  playerName.readOnly = true;
  element<HTMLButtonElement>("logout-button").addEventListener(
    "click",
    () => {
      network.close();
      void request<void>(network.getApiUrl("/auth/logout"), {
        method: "POST",
      }).finally(() => window.location.reload());
    },
    { once: true },
  );
}

async function request<T>(
  url: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers: init.body
      ? {
          "content-type": "application/json",
          ...init.headers,
        }
      : init.headers,
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const payload = (await response.json().catch(() => ({
    ok: false,
    message: "账号服务返回了无法识别的响应。",
  }))) as T | ErrorResponse;
  if (!response.ok) {
    const errorPayload = payload as ErrorResponse;
    throw new Error(
      errorPayload.message
        ? errorPayload.message
        : `账号请求失败（${response.status}）。`,
    );
  }
  return payload as T;
}

function setFormsDisabled(disabled: boolean): void {
  for (const control of document.querySelectorAll<
    HTMLInputElement | HTMLButtonElement
  >("#auth-panel input, #auth-panel button")) {
    control.disabled = disabled;
  }
}

function messageFor(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "无法连接账号服务，请稍后重试。";
}

function element<T extends HTMLElement>(id: string): T {
  const value = document.getElementById(id);
  if (!value) {
    throw new Error(`Missing element #${id}`);
  }
  return value as T;
}
